/**
 * AI CONTEXT
 * Purpose: notification query + mutation hooks (active/archived lists, read/archive actions, toast effect).
 * Data: filtered notification subscriptions sorted client-side.
 * Exposes: useUserNotifications, useArchivedNotifications, useNotificationActions, useNotificationToastEffect.
 * Used by: notification center and debug dashboard.
 * Refactor: move repeated update payload construction into internal helper utilities.
 */
import dayjs from "dayjs"
import { sortBy } from "lodash"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { notificationAtoms } from "#atoms"
import Firebase from "#classes/Firebase"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { NotificationConstructor } from "#customTypes/composedTypes/notificationTypes"
import { Uuid } from "#customTypes/globalTypes"

import { useFilteredDocs } from "./useSubscription"

/**
 * Subscribe to all non-archived notifications for the given user.
 * Returns the list sorted newest-first, and a derived unread count.
 *
 * We intentionally omit `orderBy` from the Firestore query to avoid
 * requiring a composite index on (recipientId, archived, createdAt).
 * Sorting is done client-side instead.
 */
export function useUserNotifications(userId: Uuid | undefined | null) {
  const queryConfig: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        recipientId: { value: userId ?? "__none__" },
        archived: { value: false },
      },
    }),
    [userId]
  )

  const raw = useFilteredDocs<NotificationConstructor>(
    notificationAtoms,
    DOC_TYPES.NOTIFICATIONS,
    queryConfig
  )

  const notifications = useMemo(
    () => sortBy(raw, (n) => n.createdAt).reverse(),
    [raw]
  )

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  )

  return { notifications, unreadCount }
}

/**
 * Subscribe to archived notifications for the given user.
 */
export function useArchivedNotifications(userId: Uuid | undefined | null) {
  const queryConfig: FirebaseQueryConfig = useMemo(
    () => ({
      queries: {
        recipientId: { value: userId ?? "__none__" },
        archived: { value: true },
      },
    }),
    [userId]
  )

  const raw = useFilteredDocs<NotificationConstructor>(
    notificationAtoms,
    DOC_TYPES.NOTIFICATIONS,
    queryConfig
  )

  return useMemo(() => sortBy(raw, (n) => n.createdAt).reverse(), [raw])
}

/**
 * Actions for mutating notifications: toggle read, mark all read, archive, archive all.
 */
export function useNotificationActions() {
  const { currentUserId } = useUserContext()

  const toggleRead = useCallback(
    async (notification: NotificationConstructor) => {
      if (!currentUserId) return
      const nowRead = !notification.read
      await Firebase.getInstance().updateDocument(
        DOC_TYPES.NOTIFICATIONS,
        notification.id,
        {
          read: nowRead,
          readAt: nowRead ? new Date().toISOString() : null,
          lastUpdatedBy: currentUserId,
          lastUpdatedAt: new Date().toISOString(),
        }
      )
    },
    [currentUserId]
  )

  const markAllAsRead = useCallback(
    async (notifications: NotificationConstructor[]) => {
      if (!currentUserId) return
      const unread = notifications.filter((n) => !n.read)
      if (unread.length === 0) return

      const now = new Date().toISOString()
      const batchData: Record<Uuid, Partial<NotificationConstructor>> = {}
      for (const n of unread) {
        batchData[n.id] = {
          read: true,
          readAt: now,
          lastUpdatedBy: currentUserId,
          lastUpdatedAt: now,
        }
      }
      await Firebase.getInstance().updateDocuments({
        [DOC_TYPES.NOTIFICATIONS]: batchData,
      })
    },
    [currentUserId]
  )

  const archiveNotification = useCallback(
    async (id: Uuid) => {
      if (!currentUserId) return
      const now = new Date().toISOString()
      await Firebase.getInstance().updateDocument(DOC_TYPES.NOTIFICATIONS, id, {
        archived: true,
        archivedAt: now,
        read: true,
        readAt: now,
        lastUpdatedBy: currentUserId,
        lastUpdatedAt: now,
      })
    },
    [currentUserId]
  )

  const archiveAll = useCallback(
    async (notifications: NotificationConstructor[]) => {
      if (!currentUserId) return
      const toArchive = notifications.filter((n) => !n.archived)
      if (toArchive.length === 0) return

      const now = new Date().toISOString()
      const batchData: Record<Uuid, Partial<NotificationConstructor>> = {}
      for (const n of toArchive) {
        batchData[n.id] = {
          archived: true,
          archivedAt: now,
          read: true,
          readAt: now,
          lastUpdatedBy: currentUserId,
          lastUpdatedAt: now,
        }
      }
      await Firebase.getInstance().updateDocuments({
        [DOC_TYPES.NOTIFICATIONS]: batchData,
      })
    },
    [currentUserId]
  )

  return { toggleRead, markAllAsRead, archiveNotification, archiveAll }
}

const INITIAL_TOAST_MAX_AGE_MINUTES = 60
const INITIAL_TOAST_MAX_COUNT = 3

/**
 * Fires Ant Design toast notifications when new unread items arrive.
 *
 * On initial load (login), only toasts the most recent unread notifications
 * that are younger than INITIAL_TOAST_MAX_AGE_MINUTES, capped at
 * INITIAL_TOAST_MAX_COUNT. Everything else silently appears in the badge.
 *
 * After initial load, any newly-arriving unread notification toasts immediately.
 */
export function useNotificationToastEffect(
  notifications: NotificationConstructor[],
  showToast: (notification: NotificationConstructor) => void
) {
  const seenIdsRef = useRef<Set<string>>(new Set())
  const initialLoadRef = useRef(true)

  useEffect(() => {
    const currentIds = new Set(notifications.map((n) => n.id))

    if (initialLoadRef.current) {
      const cutoff = dayjs()
        .subtract(INITIAL_TOAST_MAX_AGE_MINUTES, "minute")
        .toISOString()
      const recent = notifications
        .filter((n) => !n.read && n.createdAt > cutoff)
        .slice(0, INITIAL_TOAST_MAX_COUNT)
      for (const n of recent) {
        showToast(n)
      }
      seenIdsRef.current = currentIds
      initialLoadRef.current = false
      return
    }

    for (const n of notifications) {
      if (!n.read && !seenIdsRef.current.has(n.id)) {
        showToast(n)
      }
    }

    seenIdsRef.current = currentIds
  }, [notifications, showToast])
}
